<template>
<div :class="['plan-summary', this.currentView.context, { 'cal-open': this.isCalVisible }]" v-if="this.dailySummary">
  <div class="calendar-container">
    <div class="calendar-label" @click="toggleCal">
      <h3>
        <small>
          <BaseIcon name="calendar" /> <span class="week-title">{{ this.weekTitle }}</span>
          <BaseIcon name="chevron-down" v-if="!this.isCalVisible" />
          <BaseIcon name="chevron-up" v-else />
        </small>
      </h3>
    </div>

    <transition name="fade">
      <div class="week-row" v-show="!this.isCalVisible">
        <div class="day-col" v-for="(day, i) in this.dailySummary.planDays" :key="i">
          <h3 class="day-name"
            ><small>{{ dayName(day) }}</small></h3
          >
          <div class="day-date-wrapper" @click="selectDay(day, i)">
            <h3 :id="`dayNum-${i}`" :class="{ selected: (hasSelectedCalDate && showDayIndicator(day)) || showDayIndicator(day) }">{{ dayNum(day) }}</h3>
            <div class="dishes-indicator" v-if="dayHasDishes(day)">&nbsp;</div>
            <div :id="`indicator-${i}`" :class="['day-indicator', { selected: (hasSelectedCalDate && showDayIndicator(day)) || showDayIndicator(day) }]"
              >&nbsp;</div
            >
          </div>
        </div>
      </div>
    </transition>

    <div v-show="this.isCalVisible">
      <v-calendar
        :rows="3"
        :step="1"
        :min-date="this.calRange.minDate"
        :max-date="this.calRange.maxDate"
        :attributes="this.daysWithDishes"
        @dayclick="selectCalDate"
        transition="none"
        nav-visibility="hidden"
        is-expanded
        :is-dark="isDark"
        :locale="this.$i18n.locale"
      />
    </div>
  </div>

  <div class="selected-datestamp" v-if="dateStamp">
    <h4>{{ dateStamp.month }} {{ dateStamp.date }}</h4>
    <h1>{{ dateStamp.day }}</h1>
  </div>

  <!-- MORNING / BREAKFAST -->

  <span :class="['slots-container', { 'cal-open': this.isCalVisible }]">
    <div :class="['mealtime-wrapper', { 'screen-lg': onTabletUp, open: this.morningSelected || morningVisible }]" v-b-toggle="'accordion-Breakfast'">
      <div class="mealtime-list-item" @click="selectItem('morning')">
        <div class="item-icon">
          <img class="icon-mealtime" src="@assets/images/icon-morning-color.svg" />
        </div>
        <div class="item-name">{{ $t('lblMorning') }}</div>
        <span class="recipe-count" v-if="breakfastDishes || (this.dailySummary.day && this.dailySummary.day.Breakfast.leftovers)">
          {{ breakfastDishes.concat(this.dailySummary.day.Breakfast.leftovers).length }} {{ $t('lblRecipes') }}
        </span>
        <BaseIcon class="icon-chevron" name="chevron-up" v-if="this.morningVisible || (this.morningSelected && !this.dishToAdd)" />
        <BaseIcon class="icon-chevron" name="chevron-down" v-if="!this.morningVisible && !this.morningSelected && !this.dishToAdd" />
        <span v-if="this.dishToAdd">
          <BaseIcon class="icon-add-dish alt" name="plus-circle" />
        </span>
      </div>

      <b-collapse class="mealtime-detail-wrapper" v-model="morningVisible" id="accordion-Breakfast" accordion="mealtime">
        <b-card>
          <PlanLeftoversSlot
            :appliedMealtime="'Breakfast'"
            v-if="this.dailySummary && this.dailySummary.day && breakfastLeftoverOptions && breakfastLeftoverOptions.length"
            @showLeftoverOptions="showLeftoverOptions($event)"
            @showLeftoverDetails="showLeftoverDetails($event)"
          />
          <PlanEmptySlot :mealtime="this.$t('lblMorning')" v-if="isBreakfastEmpty" @show-recipes="showAllRecipes('Breakfast', 'Morning')" />

          <div class="swipeable">
            <MealCard
              v-for="(dish, i) in breakfastDishes"
              :key="dish.type + '-' + dish.id + '-' + `Morning-${i}`"
              :dish="dish"
              :cardType="'minimal'"
              :summary="true"
              :appliedMealtime="'Breakfast'"
              @showDishDetails="showDishDetails(dish, 'Morning', 'Breakfast')"
              @remove-dish="onRemoveDish($event)"
            />

            <BaseButton type="button" class="btn-add-recipe" v-if="!isBreakfastEmpty" @click.stop="showAllRecipes('Breakfast', 'Morning')">
              <BaseIcon class="icon-add-dish" name="plus" />{{ $t('calender.lblAddRecipe') }}
            </BaseButton>
          </div>
        </b-card>
      </b-collapse>
    </div>

    <!-- MIDDAY / LUNCH -->

    <div :class="['mealtime-wrapper', { 'screen-lg': onTabletUp, open: this.middaySelected }]" v-b-toggle="'accordion-Lunch'">
      <div class="mealtime-list-item" @click="selectItem('midday')">
        <div class="item-icon">
          <img class="icon-mealtime" src="@assets/images/icon-midday-color.svg" />
        </div>
        <div class="item-name">{{ $t('lblAfternoon') }}</div>
        <span class="recipe-count" v-if="lunchDishes || (this.dailySummary.day && this.dailySummary.day.Lunch.leftovers)">
          {{ lunchDishes.concat(this.dailySummary.day.Lunch.leftovers).length }} {{ $t('lblRecipes') }}
        </span>
        <BaseIcon class="icon-chevron" name="chevron-up" v-if="this.middayVisible || (this.middaySelected && !this.dishToAdd)" />
        <BaseIcon class="icon-chevron" name="chevron-down" v-if="!this.middayVisible && !this.middaySelected && !this.dishToAdd" />
        <span v-if="this.dishToAdd">
          <BaseIcon class="icon-add-dish alt" name="plus-circle" />
        </span>
      </div>

      <b-collapse class="mealtime-detail-wrapper" v-model="middayVisible" id="accordion-Lunch" accordion="mealtime">
        <b-card>
          <PlanLeftoversSlot
            :appliedMealtime="'Lunch'"
            v-if="this.dailySummary && this.dailySummary.day && lunchLeftoverOptions && lunchLeftoverOptions.length"
            @showLeftoverOptions="showLeftoverOptions($event)"
            @showLeftoverDetails="showLeftoverDetails($event)"
          />
          <PlanEmptySlot :mealtime="this.$t('lblAfternoon')" v-if="isLunchEmpty" @show-recipes="showAllRecipes('Lunch', 'Midday')" />

          <div class="swipeable">
            <MealCard
              v-for="(dish, i) in lunchDishes"
              :key="dish.type + '-' + dish.id + '-' + `Midday-${i}`"
              :dish="dish"
              :cardType="'minimal'"
              :summary="true"
              :appliedMealtime="'Lunch'"
              @showDishDetails="showDishDetails(dish, 'Midday', 'Lunch')"
              @remove-dish="onRemoveDish($event)"
            />

            <BaseButton type="button" class="btn-add-recipe" v-if="!isLunchEmpty" @click.stop="showAllRecipes('Lunch', 'Midday')">
              <BaseIcon class="icon-add-dish" name="plus" />
              {{ $t('calender.lblAddRecipe') }}
            </BaseButton>
          </div>
        </b-card>
      </b-collapse>
    </div>

    <!-- EVENING / DINNER -->

    <div :class="['mealtime-wrapper', { 'screen-lg': onTabletUp, open: this.eveningSelected }]" v-b-toggle="'accordion-Dinner'">
      <div class="mealtime-list-item" @click="selectItem('evening')">
        <div class="item-icon">
          <img class="icon-mealtime" src="@assets/images/icon-evening-color.svg" />
        </div>
        <div class="item-name">{{ $t('lblEvening') }}</div>
        <span class="recipe-count" v-if="dinnerDishes || (this.dailySummary.day && this.dailySummary.day.Dinner.leftovers)">
          {{ dinnerDishes.concat(this.dailySummary.day.Dinner.leftovers).length }} {{ $t('lblRecipes') }}
        </span>
        <BaseIcon class="icon-chevron" name="chevron-up" v-if="this.eveningVisible || (this.eveningSelected && !this.dishToAdd)" />
        <BaseIcon class="icon-chevron" name="chevron-down" v-if="!this.eveningVisible && !this.eveningSelected && !this.dishToAdd" />
        <span v-if="this.dishToAdd">
          <BaseIcon class="icon-add-dish alt" name="plus-circle" />
        </span>
      </div>

      <b-collapse class="mealtime-detail-wrapper" v-model="eveningVisible" id="accordion-Dinner" accordion="mealtime">
        <b-card>
          <div class="leftovers-group" v-if="isDinnerEmpty || (dinnerLeftoverOptions && dinnerLeftoverOptions.length)">
            <PlanLeftoversSlot
              :appliedMealtime="'Dinner'"
              v-if="this.dailySummary && this.dailySummary.day && dinnerLeftoverOptions && dinnerLeftoverOptions.length"
              @showLeftoverOptions="showLeftoverOptions($event)"
              @showLeftoverDetails="showLeftoverDetails($event)"
            />
            <PlanEmptySlot :mealtime="this.$t('lblEvening')" v-if="isDinnerEmpty" @show-recipes="showAllRecipes('Dinner', 'Evening')" />
          </div>

          <div
            class="swipeable"
            @click.stop="
              () => {
                return;
              }
            "
          >
            <MealCard
              v-for="(dish, i) in dinnerDishes"
              :key="dish.type + '-' + dish.id + '-' + `Evening-${i}`"
              :dish="dish"
              :cardType="'minimal'"
              :summary="true"
              :appliedMealtime="'Dinner'"
              @showDishDetails="showDishDetails(dish, 'Evening', 'Dinner')"
              @remove-dish="onRemoveDish($event)"
            />

            <BaseButton type="button" class="btn-add-recipe" v-if="!isDinnerEmpty" @click.stop="showAllRecipes('Dinner', 'Evening')">
              <BaseIcon class="icon-add-dish" name="plus" />
              {{ $t('calender.lblAddRecipe') }}
            </BaseButton>
          </div>
        </b-card>
      </b-collapse>
    </div>
  </span>

  <template v-if="hasMacros">
    <MacroTotals class="macros-container" :chartType="'bar'" :macros="this.dailySummary.day.aggregatedNutritionDigest" />
    <div class="calories-summary">
      <p class="label">
        {{ $t('todaysCalories') }}
      </p>
      <p class="calories-value">
        {{ dailySummary.day.aggregatedNutritionDigest.macroNutritionDigest.calories.value }}
      </p>
    </div>
  </template>
  <div class="macros-empty-container" v-else>
    <p>{{ $t('planSummaryHelpText') }}</p>
  </div>
</div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import utils from '@src/utils/utils';
import MacroTotals from '../Macros/MacroTotals';
import MealCard from '../MealCard/MealCard';
import PlanEmptySlot from './PlanEmptySlot';
import PlanLeftoversSlot from './PlanLeftoversSlot';
import { planMixin } from 'planMixin';
import { shareMixin } from 'shareMixin';
import {
  planComputed,
  planMethods,
  dishesComputed,
  dishesMethods,
  kitchenDrawerComputed,
  kitchenDrawerMethods,
  mealsComputed,
  mealsMethods,
  recipesComputed,
  recipesMethods,
  mainMethods,
  authComputed,
} from '@state/helpers';

export default {
  mixins: [planMixin, shareMixin],
  components: {
    MacroTotals,
    MealCard,
    PlanEmptySlot,
    PlanLeftoversSlot,
  },
  props: {
    isEmbed: {
      type: Boolean,
    },
    onTabletUp: {
      type: Boolean,
    },
  },
  data() {
    return {
      dishDetails: {},
      removingMorningDish: false,
      removingMiddayDish: false,
      removingEveningDish: false,
      morningSelected: false,
      morningVisible: false,
      middaySelected: false,
      middayVisible: false,
      eveningSelected: false,
      eveningVisible: false,
      daysWithDishes: [],
      isDark: false,
      calRange: {
        minDate: `${moment()
          .subtract(1, 'month')
          .startOf('month')
          .format('M/D/YYYY')}`,
        maxDate: `${moment()
          .add(1, 'month')
          .endOf('month')
          .format('M/D/YYYY')}`,
      },
      weekTitle: this.$t('lblThisWeek'),
      didOpenNavigateDeeper: false,
    };
  },
  computed: {
    ...planComputed,
    ...dishesComputed,
    ...kitchenDrawerComputed,
    ...mealsComputed,
    ...recipesComputed,
    ...authComputed,
    breakfastDishes() {
      if (this.dailySummary && this.dailySummary.day) {
        return this.dailySummary.day.Breakfast.meals.concat(this.dailySummary.day.Breakfast.recipes);
      }
    },
    lunchDishes() {
      if (this.dailySummary && this.dailySummary.day) {
        return this.dailySummary.day.Lunch.meals.concat(this.dailySummary.day.Lunch.recipes);
      }
    },
    dinnerDishes() {
      if (this.dailySummary && this.dailySummary.day) {
        return this.dailySummary.day.Dinner.meals.concat(this.dailySummary.day.Dinner.recipes);
      }
    },
    allDishes() {
      if (this.breakfastDishes) {
        return this.breakfastDishes.concat(this.lunchDishes).concat(this.dinnerDishes);
      }
    },
    breakfastLeftoverOptions() {
      if (this.dailySummary && this.dailySummary.day) {
        return this.dailySummary.day.Breakfast.leftoverOptions;
      }
    },
    lunchLeftoverOptions() {
      if (this.dailySummary && this.dailySummary.day) {
        return this.dailySummary.day.Lunch.leftoverOptions;
      }
    },
    dinnerLeftoverOptions() {
      if (this.dailySummary && this.dailySummary.day) {
        return this.dailySummary.day.Dinner.leftoverOptions;
      }
    },
    currentView() {
      return this.navContent[this.activeNavIndex];
    },
    isBreakfastEmpty() {
      if (this.breakfastDishes) {
        return !this.breakfastDishes.length && !this.removingMorningDish;
      }
    },
    isLunchEmpty() {
      if (this.lunchDishes) {
        return !this.lunchDishes.length && !this.removingMiddayDish;
      }
    },
    isDinnerEmpty() {
      if (this.dinnerDishes) {
        return !this.dinnerDishes.length && !this.removingEveningDish;
      }
    },
    hasMacros() {
      // This is what changed. Should deploy
      return this.dailySummary.day &&
        this.dailySummary.day.aggregatedNutritionDigest &&
        this.dailySummary.day.aggregatedNutritionDigest.macroNutritionDigest.calories &&
        this.dailySummary.day.aggregatedNutritionDigest.recipesWithNutrition > 0
        ? true
        : false;
    },
    dateStamp() {
      if (this.dailySummary && this.dailySummary.day) {
        const dateString = this.dailySummary.day.dateString;
        const selectedDate = dateString.substring(0, dateString.indexOf('T'));
        return {
          month: moment(selectedDate)
            .locale(this.$i18n.locale)
            .format('MMMM'),
          date: moment(selectedDate)
            .locale(this.$i18n.locale)
            .format('D'),
          day: moment(selectedDate)
            .locale(this.$i18n.locale)
            .format('dddd'),
        };
      }
    },
  },
  mounted() {
    this.populateCal();
    if (this.allDishes) {
      this.allDishes.forEach(async (dish) => {
        this.dishDetails[dish.dishId] = await this.getPlannedDish(dish.dishId);
      });
    }
    this.isDark = this.currentUser.client.name === 'gunnar' ? true : false;

    // Open proper accordian
    var today = new Date();
    var time = today.getHours();

    if (time > 0 && time < 11) {
      // Morning
      this.morningSelected = true;
      this.morningVisible = true;
    } else if (time >= 11 && time <= 15) {
      // Lunch
      this.middaySelected = true;
      this.middayVisible = true;
    } else if (time > 15) {
      // Dinner
      this.eveningSelected = true;
      this.eveningVisible = true;
    }
  },
  methods: {
    ...planMethods,
    ...dishesMethods,
    ...kitchenDrawerMethods,
    ...mealsMethods,
    ...recipesMethods,
    ...mainMethods,
    selectDaySlot(mealtime) {
      if (this.dailySummary && this.dailySummary.day) {
        this.setSelectedDaySlot({
          day: this.dailySummary.day,
          mealTime: this.getMealTime(mealtime),
          name: mealtime,
        });
      }
    },

    onRemoveDish(slot) {
      const clone = _.cloneDeep(this.dailySummary);
      if (slot.dish.dishType === 'r') {
        clone.day[slot.mealtime].recipes = clone.day[slot.mealtime].recipes.filter((dish) => dish.dishId !== slot.dish.dishId);
      } else {
        clone.day[slot.mealtime].meals = clone.day[slot.mealtime].meals.filter((dish) => dish.dishId !== slot.dish.dishId);
      }
      this.setDailySummary(clone);
    },
    toggleDayIndicator(i) {
      if (document.getElementById(`dayNum-${i}`) !== null) {
        document.getElementById(`dayNum-${i}`).style.color = 'white';
        document.getElementById(`indicator-${i}`).style.display = 'block';

        for (let j = 0; j < this.dailySummary.planDays.length; j++) {
          if (j !== i) {
            document.getElementById(`dayNum-${j}`).style.color = '#030C16';
            document.getElementById(`indicator-${j}`).style.display = 'none';
          }
        }
      }
    },
    selectDay(day, i) {
      if (day) {
        const dateString = day.dateString.substring(0, day.dateString.indexOf('T'));
        this.setDailySummary({ ...this.dailySummary, day, selectedDate: dateString });
        this.setOtherDaySelected();
      }
    },
    async showDishDetails(dish, timeOfDay, propName) {
      if (this.didOpenNavigateDeeper) {
        return;
      }
      this.setDidOpenNavigateDeeper(true);
      let usersDish = null;
      if (!this.dishDetails[dish.dishId]) {
        usersDish = await this.getPlannedDish(dish.dishId);
      } else {
        usersDish = this.dishDetails[dish.dishId];
      }

      if (!usersDish.dishType) {
        const errOpts = { icon: 'exclamation', type: 'error', theme: 'bubble', singleton: true, duration: 5000 };
        this.$toasted.show('The recipe could not be loaded. Please try again later.', errOpts);
      } else {
        if (!this.onTabletUp && !this.isOpen) {
          this.toggleDrawer();
        }

        if (usersDish.dishType === 'r') {
          this.updateShareableUrl('RecipeDetail', dish);
          this.navigateDeeper({
            title: `${this.dailySummary.day.header.split(' ')[0]} ${timeOfDay}`,
            component: 'RecipeDetail',
            context: this.onTabletUp ? 'drawerModal' : null,
            data: {
              ...usersDish,
              dishId: dish.dishId,
              isPlanned: true,
              onRevision: (newDish) => {
                this.dishDetails[dish.dishId] = newDish;
              },
              onRemove: (dishId) => {
                const clone = _.cloneDeep(this.dailySummary);
                clone.day[propName].recipes = clone.day[propName].recipes.filter((recipe) => recipe.dishId !== dishId);
                this.setDailySummary(clone);

                delete this.dishDetails[dishId];
              },
            },
          });
        } else if (usersDish.dishType === 'm') {
          this.updateShareableUrl('MealDetail', dish);
          this.navigateDeeper({
            title: `${this.dailySummary.day.header.split(' ')[0]} ${timeOfDay}`,
            component: 'MealDetail',
            context: this.onTabletUp ? 'drawerModal' : null,
            data: {
              ...usersDish,
              dishId: dish.dishId,
              isPlanned: true,
              onRevision: (newDish) => {
                this.dishDetails[dish.dishId] = newDish;
              },
              onRemove: (dishId) => {
                const clone = _.cloneDeep(this.dailySummary);
                clone.day[propName].meals = clone.day[propName].meals.filter((meal) => meal.dishId !== dishId);
                this.setDailySummary(clone);

                delete this.dishDetails[dishId];
              },
            },
          });
        }
      }
    },
    setDidOpenNavigateDeeper(value) {
      // prevents from opening view multiple times with multiples clicks
      this.didOpenNavigateDeeper = value;
      setTimeout(() => {
        if (value) {
          this.didOpenNavigateDeeper = false;
        }
      }, 700);
    },
    showLeftoverDetails(leftover) {
      if (!this.onTabletUp) {
        this.toggleDrawer();
      }

      this.navigateDeeper({
        title: 'Leftover',
        component: 'RecipeDetail',
        context: this.onTabletUp ? 'drawerModal' : null,
        data: {
          ...leftover,
          isLeftover: true,
          onRemove: (dishId) => {
            // TODO: Find out why this commented out...
            // const clone = _.cloneDeep(this.dailySummary);
            // clone.day[propName].recipes = clone.day[propName].recipes.filter((recipe) => recipe.dishId !== dishId);
            // this.setDailySummary(clone);
            // delete this.dishDetails[dishId];
          },
        },
      });
    },
    showLeftoverOptions(mealtime) {
      this.selectDaySlot(mealtime);
      this.toggleDrawer();
      this.navigateDeeper({
        title: 'Select Leftover Dish',
        component: 'PlanLeftoverOptions',
        context: this.onTabletUp ? 'drawerModal' : null,
        data: {
          mealTime: mealtime,
        },
      });
    },
    openMealtimeSlot(mealtime) {
      this.selectItem(mealtime);
      this.$root.$emit('bv::toggle::collapse', `accordion-${mealtime}`);
    },
    async showAllRecipes(mealtime, mealtimeDisplayString) {
      if (mealtime === 'Breakfast') {
        this.setFilterQueryString({
          includeMealTimes: [1],
          includeKitchens: [],
          includeTags: [],
          includeDiets: [],
          includeHealths: [],
        });
        this.getDishes();
      }

      this.selectDaySlot(mealtime);

      if (!this.onTabletUp) {
        this.toggleDrawer();
      }

      this.navigateDeeper({
        title: `${this.dailySummary.day.header.split(' ')[0] + ' ' + mealtimeDisplayString}`,
        component: 'Recipes',
        context: 'drawerModal',
        data: {
          navigateHigherWhenDone: true,
          // TODO: Find out why this commented out
          // leftoverOptionsCount: this.dailySummary.day[mealtime].leftoverOptions.length,
          // mealTime: mealtime,
        },
      });
    },
    selectItem(mealtime) {
      switch (mealtime.toLowerCase()) {
        case 'morning':
        case 'breakfast':
          this.morningSelected = !this.morningSelected;
          this.morningVisible = !this.morningVisible;
          this.middaySelected = false;
          this.eveningSelected = false;

          if (this.dishToAdd) {
            this.add('Breakfast');
          }
          break;
        case 'midday':
        case 'lunch':
          this.middaySelected = !this.middaySelected;
          this.middayVisible = !this.middayVisible;
          this.morningSelected = false;
          this.eveningSelected = false;

          if (this.dishToAdd) {
            this.add('Lunch');
          }
          break;
        case 'evening':
        case 'dinner':
          this.eveningSelected = !this.eveningSelected;
          this.eveningVisible = !this.eveningVisible;
          this.morningSelected = false;
          this.middaySelected = false;

          if (this.dishToAdd) {
            this.add('Dinner');
          }
          break;
      }
    },
    async add(mealtime) {
      if (!this.dailySummary) {
        window.history.replaceState(null, '', window.location.pathname);
        return;
      }
      if (this.dishToAdd) {
        const payload = {
          ...this.dishToAdd,
          date: this.dailySummary.day.dateString,
          planDayId: this.dailySummary.day.id ? this.dailySummary.day.id : null,
          planMealTime: this.getMealTime(mealtime),
        };
        await this.addDish(payload);
        // TODO: This will need to be dynamic when the calendar is added back in.
        this.getPlanGridByWeekOffset({ today: this.dailySummary.day.dateString, isPremium: true, weekOffset: 0 }).finally(() => {
          const selectedDayDateString = this.dailySummary.day.dateString.substring(0, this.dailySummary.day.dateString.indexOf('T'));

          this.setDailySummary({
            day: this.dailySummary.day,
            planDays: this.currentPlan.planDays,
            selectedDate: selectedDayDateString,
          });

          if (this.onTabletUp === undefined || this.onTabletUp) {
            return this.getGroceryList();
          }
        });
        this.setDishToAdd(null);
      }
    },
    dayName(day) {
      const dayName = day.header.split(' ');
      dayName.pop();
      return this.$t(`calender.days.${dayName[0]}`);
    },
    dayNum(day) {
      return day.header.split(' ').pop();
    },
    dayHasDishes(day) {
      if (day) {
        const breakfastDishCount = day.Breakfast.meals.concat(day.Breakfast.recipes, day.Breakfast.leftovers).length;
        const lunchDishCount = day.Lunch.meals.concat(day.Lunch.recipes, day.Lunch.leftovers).length;
        const dinnerDishCount = day.Dinner.meals.concat(day.Dinner.recipes, day.Dinner.leftovers).length;
        return breakfastDishCount || lunchDishCount || dinnerDishCount ? true : false;
      }
    },
    toggleCal() {
      if (this.dailySummary && this.dailySummary.day) {
        this.setIsCalVisible();
        document.getElementsByClassName('calendar-container')[0].scrollTop = 0;
      }
    },
    highlightToday() {
      this.daysWithDishes.push({
        key: 'today',
        highlight: {
          color: 'gray',
          fillMode: 'light',
        },
        dates: new Date(),
      });
    },
    populateCal() {
      if (this.dailySummary && this.dailySummary.planDays) {
        this.dailySummary.planDays.forEach((day) => {
          if (this.dayHasDishes(day)) {
            this.daysWithDishes.push({
              key: day.id,
              dot: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
              dates: new Date(moment(day.dateString.substring(0, day.dateString.indexOf('T'))).format('MM/DD/YYYY')),
            });
          }
        });
        this.highlightToday();
      } else {
        this.highlightToday();
      }
    },
    getWeekDiff(day) {
      const startOfSelectedWeek = moment(day).startOf('week');
      const startOfThisWeek = moment().startOf('week');
      return startOfSelectedWeek.diff(startOfThisWeek, 'weeks');
    },
    async selectCalDate(day) {
      this.showLoader(this.$t('lblPreparingYourPlanDetail'));
      const weekDiff = this.getWeekDiff(day.date);
      this.setDailySummary({ ...this.dailySummary, selectedDate: day.id });
      this.setIsCalVisible();

      await this.getPlanGridByWeekOffset({
        today: utils.setToMidNight(new Date()).toISOString(),
        isPremium: true,
        weekOffset: weekDiff,
      }).then(() => {
        this.setDailySummary({ ...this.dailySummary, selectedDate: day.id });
        this.hideLoader();
      });
    },
    setWeekTitle(day) {
      const weekDiff = this.getWeekDiff(day);

      switch (weekDiff) {
        case -1:
          this.weekTitle = this.$t('lblLastWeek');
          break;
        case 0:
          this.weekTitle = this.$t('lblThisWeek');
          break;
        case 1:
          this.weekTitle = this.$t('lblNextWeek');
          break;
        default:
          this.weekTitle = this.dailySummary.weekOf;
          break;
      }
    },
    showDayIndicator(day) {
      let dateString;

      if (this.dailySummary.selectedDate) {
        dateString = day.dateString.substring(0, day.dateString.indexOf('T'));
        return this.dailySummary.selectedDate === dateString ? true : false;
      } else {
        dateString = day.dateString.substring(0, day.dateString.indexOf('T'));
        return moment(dateString).isSame(moment(), 'day') ? true : false;
      }
    },
    hasSelectedCalDate() {
      return this.dailySummary && this.dailySummary.selectDate ? true : false;
    },
  },
  watch: {
    dailySummary(newVal, oldVal) {
      if (newVal && newVal.day) {
        this.populateCal();
        this.setWeekTitle(newVal.day.dateString);
      } else {
        console.log('Daily Summary Error: ', newVal);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.plan-summary {
  @extend %scrollable-drawer-content;

  background-color: $color-plan-summary-bg;
  padding: 0.6rem 0 11rem;
  padding-top: 0;

  .calendar-container {
    background-color: $color-grocery-list-item-bg;
    border-bottom: 1px solid $base-border-color;
    padding: 1rem;
    position: sticky;
    top: 0;
    transition: height 0.6s;
    z-index: 1;

    .calendar-label {
      display: flex;
      justify-content: center;

      h3:hover {
        cursor: pointer;
      }

      span.week-title {
        margin: 0 1rem;
      }
    }

    .week-row {
      display: flex;
      justify-content: space-around;
      padding-bottom: 1rem;

      .day-col {
        text-align: center;
      }

      .day-name {
        margin-bottom: 1rem;

        small {
          font-size: 1.1rem;
          font-weight: 600;
          opacity: 0.5;
          text-transform: uppercase;
        }
      }

      .day-date-wrapper {
        align-items: center;
        display: flex;
        justify-content: center;

        :hover {
          cursor: pointer;
        }

        .dishes-indicator {
          border: 2.5px solid;
          border-color: rgba($color-text, 0.2);
          border-radius: 50%;
          height: 3.5rem;
          position: absolute;
          transform: rotateZ(-45deg);
          width: 3.5rem;

          &.light {
            border-color: lighten($color-grey, 60);
          }
        }

        .day-indicator {
          background-color: $color-day-indicador;
          border-radius: 50%;
          display: none;
          height: 2.7rem;
          position: absolute;
          width: 2.7rem;

          &.light {
            background-color: lighten($color-grey, 60);
          }

          &.selected {
            display: block !important;
          }
        }

        h3 {
          font-size: 1.4rem;
          margin: 0.1rem 0 0;
          position: relative;
          z-index: 1;

          &.selected {
            color: white !important;
          }
        }
      }
    }
  }

  .selected-datestamp {
    align-items: center;
    display: flex;
    flex-direction: column;
    padding: 1rem;

    h4 {
      font-size: 1rem;
      line-height: 1;
      margin-bottom: 0.5rem;
      text-transform: uppercase;
    }

    h1 {
      font-size: 2.6rem;
      font-weight: 900;
      margin-bottom: 0;
    }
  }

  .macros-container {
    padding: 0 1rem;
  }

  .calories-summary {
    align-items: center;
    background: rgba($color-action, 0.1);
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    padding: 8px 24px;
    width: max-content;
    .label {
      font-size: 1.2rem;
      font-style: normal;
      letter-spacing: 0.14px;
      text-transform: uppercase;
    }
    .calories-value {
      font-size: 2.8rem;
      font-weight: bold;
    }
  }

  .macros-empty-container {
    font-size: 1.2rem;
    opacity: 0.7;
    padding: 2.7rem 3rem 2rem;
    text-align: center;
  }

  .swipeable {
    display: flex;
    margin-left: -1rem;
    margin-right: -1rem;
    overflow-x: auto;
    scroll-snap-points-y: repeat(300px);
    scroll-snap-type: mandatory;
    scroll-snap-type: x mandatory;

    &::-webkit-scrollbar {
      display: none;
    }

    .meal-card-wrapper {
      &:first-child {
        margin-left: 1rem;
      }
    }

    .btn-add-recipe {
      align-items: center;
      align-self: center;
      display: flex;
      height: 3.7rem;
      justify-content: center;
      margin-left: 1rem;
      margin-right: 2rem;
      min-width: 13rem;
      padding: 0 1rem;
      scroll-snap-align: end;

      .icon-add-dish {
        font-size: 1.2rem;
        margin-right: 0.5rem;
      }
    }
  }

  .mealtime-wrapper {
    padding-left: 1rem;
    padding-right: 1rem;

    .mealtime-list-item {
      align-items: center;
      background-color: $color-grocery-list-item-bg;
      border: 1px solid $color-grocery-list-item-border;
      border-radius: $base-border-radius;
      display: flex;
      height: 7rem;
      margin-bottom: 0.5rem;
      padding: 1.25em 1em 1.25em 1em;
      transition: 0.2s;
      will-change: transform;

      &:hover,
      &:active {
        box-shadow: $color-grocery-list-item-shadow;
        cursor: pointer;
      }

      .screen-lg {
        &:active {
          box-shadow: none;
        }
      }

      .item-icon {
        margin-left: 0.75em;
        margin-right: 1.5rem;
      }

      .item-name {
        color: $color-grocery-list-item-text;
        flex: 3;
        font-size: 1.3rem;
        font-weight: 600;
        line-height: 1;

        // iPhone 6 up
        @include media('md', null) {
          font-size: 1.6rem;
        }
      }

      .recipe-count {
        font-size: 1.1rem;
        margin-right: 0.7rem;
        opacity: 0.6;
      }

      .icon-chevron {
        font-size: 1.5rem;
        width: 2.6rem;
      }

      .icon-add-dish.alt {
        color: $color-grocery-list-item-text;
        font-size: 1.5rem;
        font-weight: 400;
      }
    }

    &.open .mealtime-list-item {
      border: 0;

      &:hover {
        box-shadow: none;
      }
    }
  }

  .card {
    background-color: transparent;
    border: 0;

    .card-body {
      padding: 0;
    }
  }

  div[aria-expanded='true'] {
    background: $color-grocery-list-bg;
    border-bottom: 1px solid $base-border-color;
    border-top: 1px solid $base-border-color;
    margin-bottom: 0.8rem;
    padding-bottom: 1rem;
    width: 100%;
  }

  .mealtime-detail-wrapper {
    background: transparent;
    width: 100%;
  }

  &.drawerModal {
    height: 100%;
  }

  &.cal-open {
    .selected-datestamp {
      margin-top: 11.2rem;

      @include media('md', 'iphone-6') {
        margin-top: 11.3rem;
      }

      @include media('md', 'iphone-6-plus') {
        margin-top: 11.8rem;
      }
    }

    .calendar-container {
      background-color: transparent;
      box-shadow: 0 0 5px 1px rgba(0, 0, 0, 0.2);
      height: 38rem;
      left: 0;
      overflow-y: scroll;
      position: absolute;
      right: 0;
      z-index: 1;
    }

    .calendar-label {
      background-color: $color-grocery-list-item-bg;
      height: 4rem;
      left: 0;
      padding-top: 1rem;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 11;
    }
  }
}

// V-CALENDAR OVERRIDES

.vc-border-gray-400 {
  border: 0;
}

.vc-arrows-container {
  display: none !important;
}

/* stylelint-disable */
/deep/ .vc-text-lg {
  /* stylelint-enable */
  font-size: 1.1rem !important;
  font-weight: bold;
}

.vc-arrows-container.title-center {
  display: none !important;
}

span.day-with-dishes {
  background: white !important;
  color: black !important;
  height: 2.2rem !important;
  position: relative !important;
  top: 0.3rem !important;
  width: 2.2rem !important;
}

// ANIMATIONS
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
